import { Button, ButtonGroup, LuIcon, Popover, TextArea } from "@overmap-ai/blocks"
import { useScanner } from "@overmap-ai/forms"
import { type ChangeEventHandler, type ComponentPropsWithoutRef, memo, useCallback, useEffect, useState } from "react"

import { modelIconMapping } from "../../mappings/icons"

export type IdentifierEditorPopoverProps = ComponentPropsWithoutRef<typeof Popover.Root> &
	ComponentPropsWithoutRef<typeof Popover.Content> & {
		initialValue?: string
		onCancel?: () => void
		onSave?: (value: string) => void
	}

export const IdentifierEditorPopover = memo<IdentifierEditorPopoverProps>((props) => {
	const {
		children,
		open,
		onOpenChange,
		defaultOpen,
		modal,
		size,
		align,
		accentColor = "base",
		initialValue,
		onCancel,
		onSave,
		...rest
	} = props

	const openScanner = useScanner()
	const [value, setValue] = useState(initialValue ?? "")

	useEffect(() => {
		setValue(initialValue ?? "")
	}, [initialValue])

	const handleTextAreaChange = useCallback<ChangeEventHandler<HTMLTextAreaElement>>((e) => {
		const value = e.target.value
		setValue(value)
	}, [])

	const handleScan = useCallback(() => {
		openScanner((close) => ({
			onDecodeResult: (result) => {
				const value = result.rawValue.trim()
				setValue(value)
				close()
			},
			onClose: close,
		}))
	}, [openScanner])

	const handleCreate = useCallback(() => {
		onSave?.(value)
	}, [onSave, value])

	const handleCancel = useCallback(() => {
		onCancel?.()
	}, [onCancel])

	const handleOpenChange = useCallback(
		(open: boolean) => {
			if (!open) setValue(initialValue ?? "")
			onOpenChange?.(open)
		},
		[initialValue, onOpenChange],
	)

	return (
		<Popover.Root open={open} onOpenChange={handleOpenChange} defaultOpen={defaultOpen} modal={modal}>
			<Popover.Trigger asChild>{children}</Popover.Trigger>
			<Popover.Content
				className="flex max-w-xs flex-col gap-2"
				size={size}
				align={align}
				accentColor={accentColor}
				onFocusOutside={(e) => e.preventDefault()}
				{...rest}
			>
				<TextArea
					className="field-sizing-content w-full"
					size="xs"
					value={value}
					onChange={handleTextAreaChange}
				/>

				<ButtonGroup className="flex justify-between gap-4" size="xs" accentColor="base" variant="soft">
					<Button type="button" onClick={handleScan} variant="soft" accentColor="base">
						<LuIcon icon={modelIconMapping.scan} />
						Scan
					</Button>

					<div className="flex grow justify-end gap-2">
						<Popover.Close asChild>
							<Button type="button" onClick={handleCancel}>
								Cancel
							</Button>
						</Popover.Close>
						<Popover.Close asChild>
							<Button type="button" onClick={handleCreate}>
								Save
							</Button>
						</Popover.Close>
					</div>
				</ButtonGroup>
			</Popover.Content>
		</Popover.Root>
	)
})
IdentifierEditorPopover.displayName = "IdentifierEditorPopover"
